// ./src/hooks/useUserInvitations.ts
//
// Data hook for UserInvitation CRUD operations.
//
// Follows the same architecture as useExpenses / useUsers:
// - OpResult return type on all operations for consistent error handling
// - Race-condition protection via monotonic listCallIdRef
// - currentPageRef to avoid stale closures in loadMore
// - Mount-time refs for pageSize, initialFilters, initialSort
// - lastQueriedFiltersRef / lastQueriedSortRef for loadMore/refresh
// - OpResult integration (not plain strings)
// - isLoaded tracking
// - Memoized return object
//
// Domain-specific operations preserved:
// - resend(id) — resend invitation email
// - acceptInvitation(params) — accept an invitation (public/unauthenticated flow)

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";
import {
  UserInvitation,
  UserInvitationService,
  UserInvitationFilter,
  UserInvitationInput,
  UserInvitationAcceptInput,
  Pagination,
  SortItem,
  listPage,
} from "@/utils/api";
import { useApp } from "@/contexts";
import { DEFAULT_PAGE_SIZE } from "@/config";
import { useRefreshOnWriteTimeout } from "./useRefreshOnWriteTimeout";

// ============================================
// Types
// ============================================

export interface UseUserInvitationsOptions {
  /**
   * Page size for pagination.
   * Mount-time configuration — changes after mount have no effect.
   */
  pageSize?: number;

  /**
   * Filters applied on initial autoLoad.
   * Mount-time configuration — call `list(newFilters)` to apply new filters.
   */
  initialFilters?: Partial<UserInvitationFilter>;

  /**
   * Sort configuration applied on initial autoLoad.
   * Mount-time configuration — pass sort to `list()` to change.
   *
   * @default [{ name: "firstName", order: "ASC" }]
   */
  initialSort?: SortItem[];

  /** Auto-load data on mount (and on userId change) */
  autoLoad?: boolean;
}

export interface UseUserInvitationsReturn {
  // Data state
  userInvitation: UserInvitation | null;
  userInvitations: UserInvitation[];
  loading: boolean;
  isLoaded: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<UserInvitationFilter>;
  sortBy: SortItem[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  isResending: boolean;
  isAcceptingInvitation: boolean;
  removingId: string | null;
  resendingId: string | null;
  selectedUserInvitation: UserInvitation | null;

  // Core operations (all return OpResult)
  get: (id: string) => Promise<OpResult>;
  list: (
    filter?: Partial<UserInvitationFilter>,
    pagination?: Pagination,
    sortBy?: SortItem[],
    shouldReset?: boolean
  ) => Promise<OpResult>;
  getMany: (ids: string[]) => Promise<OpResult>;
  create: (params: UserInvitationInput) => Promise<OpResult>;
  update: (id: string, params: UserInvitationInput) => Promise<OpResult>;
  remove: (id: string) => Promise<OpResult>;

  // Domain-specific operations
  resend: (id: string) => Promise<OpResult>;
  acceptInvitation: (params: UserInvitationAcceptInput) => Promise<OpResult>;

  // Convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<OpResult>;

  // UI state management
  setSelectedUserInvitation: (invitation: UserInvitation | null) => void;

  // Helper operations
  reset: () => void;

  // Computed values
  invitationsByStatus: {
    active: UserInvitation[];
    expired: UserInvitation[];
  };
  invitationsByEmailDomain: Record<string, UserInvitation[]>;

  // Lookup helpers
  getInvitationById: (id: string | undefined) => UserInvitation | undefined;

  // Error handling
  lastError: OpResult | null;
  clearError: () => void;
}

// ============================================
// Default Values
// ============================================

const DEFAULT_SORT: SortItem[] = [{ name: "firstName", order: "ASC" }];
const DEFAULT_FILTERS: Partial<UserInvitationFilter> = {};

// ============================================
// Hook Implementation
// ============================================

export const useUserInvitations = (
  options: UseUserInvitationsOptions = {}
): UseUserInvitationsReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = DEFAULT_FILTERS,
    initialSort = DEFAULT_SORT,
    autoLoad = false,
  } = options;

  // Get user from AppContext
  const { user: appUser } = useApp();
  const userId = appUser?.id ?? null;

  // ── Mount-time configuration captured in refs ──
  const pageSizeRef = useRef(pageSize);
  const initialFiltersRef = useRef(initialFilters);
  const initialSortRef = useRef(initialSort);

  // Ref to track the last-queried filters for loadMore
  const lastQueriedFiltersRef = useRef<Partial<UserInvitationFilter>>(initialFilters);

  // Ref to track the last-queried sort for loadMore/refresh
  const lastQueriedSortRef = useRef<SortItem[]>(initialSort);

  // Monotonically increasing call ID to detect stale list() responses
  const listCallIdRef = useRef(0);

  // Track previous userId for change detection
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  // Ref to track current page — avoids stale closures in loadMore
  const currentPageRef = useRef(1);

  // ── Data state ──
  const [userInvitation, setUserInvitation] = useState<UserInvitation | null>(null);
  const [userInvitations, setUserInvitations] = useState<UserInvitation[]>([]);
  /**
   * ⚠⚠ STARTS TRUE WHEN THIS HOOK IS GOING TO AUTO-LOAD — "about to read" is not "read, and empty".
   *
   * The auto-load effect is gated on `userId`, which arrives ASYNCHRONOUSLY from `AppContext`. So on
   * a hard reload or a first navigation there are renders where no request has started yet: with
   * `loading` false and the collection empty, every list component's
   * `!loading && items.length === 0` branch is TRUE and paints the first-run empty state — "No notes
   * yet", "No tasks yet" — before the skeleton and then the data. Reported across the whole event
   * space (owner, 2026-09-28).
   * ⓘ Fixed HERE rather than in the ~45 list components that read it: the lie is in the flag, not in
   * the branch, and a per-component fix would have to be remembered by every list written after it.
   * ⚠ `autoLoad: false` still starts FALSE — a hook told not to load must not claim to be loading.
   */
  const [loading, setLoading] = useState(autoLoad);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters — tracks what was last queried
  const [filters, setFilters] = useState<Partial<UserInvitationFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortItem[]>(initialSort);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isAcceptingInvitation, setIsAcceptingInvitation] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [selectedUserInvitation, setSelectedUserInvitation] =
    useState<UserInvitation | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<OpResult | null>(null);

  // ── Computed values ──

  const isEmpty = useMemo(
    () => isLoaded && userInvitations.length === 0,
    [isLoaded, userInvitations.length]
  );

  const invitationsByStatus = useMemo(() => {
    const now = new Date();
    return userInvitations.reduce(
      (acc, invitation) => {
        const expiresAt = invitation.expiresAt
          ? new Date(invitation.expiresAt)
          : null;
        if (expiresAt && expiresAt < now) {
          acc.expired.push(invitation);
        } else {
          acc.active.push(invitation);
        }
        return acc;
      },
      { active: [] as UserInvitation[], expired: [] as UserInvitation[] }
    );
  }, [userInvitations]);

  const invitationsByEmailDomain = useMemo(() => {
    return userInvitations.reduce(
      (acc, invitation) => {
        const email = invitation.email || "";
        const domain = email.includes("@") ? email.split("@")[1] : "unknown";
        if (!acc[domain]) {
          acc[domain] = [];
        }
        acc[domain].push(invitation);
        return acc;
      },
      {} as Record<string, UserInvitation[]>
    );
  }, [userInvitations]);

  // ==========================================
  // List Helpers
  // ==========================================

  const updateOrAddInList = useCallback((updatedItem: UserInvitation) => {
    setUserInvitations((prev) => {
      const existingIndex = prev.findIndex((item) => item.id === updatedItem.id);
      if (existingIndex >= 0) {
        const newItems = [...prev];
        newItems[existingIndex] = updatedItem;
        return newItems;
      } else {
        return [updatedItem, ...prev];
      }
    });
  }, []);

  const removeFromList = useCallback((id: string) => {
    setUserInvitations((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  const reset = useCallback(() => {
    listCallIdRef.current++;

    setUserInvitation(null);
    setUserInvitations([]);
    setLastError(null);
    setLoading(false);
    setIsLoaded(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    currentPageRef.current = 1;
    setIsCreating(false);
    setIsUpdating(false);
    setIsResending(false);
    setIsAcceptingInvitation(false);
    setRemovingId(null);
    setResendingId(null);
    setSelectedUserInvitation(null);
  }, []);

  // ==========================================
  // Core Operations
  // ==========================================

  // Get single invitation
  const get = useCallback(
    async (id: string): Promise<OpResult> => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await UserInvitationService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const data = (result.data as UserInvitation[])[0];
          setUserInvitation(data);
          updateOrAddInList(data);
          setLastError(null);
          return result;
        }

        setLastError(result);
        setUserInvitation(null);
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(OpResult.fromException(error));
        setUserInvitation(null);
        return result;
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddInList]
  );

  /**
   * List invitations.
   * - shouldReset=true: replaces array (page 1 load / filter change)
   * - shouldReset=false: appends to existing (load more)
   *
   * Race-condition safe: each call gets a monotonic ID. Stale responses
   * are discarded.
   *
   * Note: Empty dependency array is safe — only accesses refs and
   * React state setters (guaranteed stable identity).
   */
  const list = useCallback(
    async (
      filter: Partial<UserInvitationFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortItem[],
      shouldReset: boolean = true
    ): Promise<OpResult> => {
      const callId = ++listCallIdRef.current;
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !shouldReset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        // A reload owns the list: a Load more it supersedes is dropped, and so is its spinner (#114).
        setLoadingMore(false);
        setCurrentPage(1);
        currentPageRef.current = 1;
      }

      setLastError(null);

      // Store queried filters/sort for loadMore and refresh
      setFilters(filter);
      lastQueriedFiltersRef.current = filter;
      if (sortByParams) {
        setSortBy(sortByParams);
        lastQueriedSortRef.current = sortByParams;
      }

      const currentSortBy = sortByParams || lastQueriedSortRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => sort);

        const result = await listPage(
          UserInvitationService.list,
          filter as UserInvitationFilter,
          paginationParams,
          sortParams
        );

        // Guard: discard stale responses
        if (callId !== listCallIdRef.current) {
          return result;
        }

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItems = Array.isArray(result.data)
            ? (result.data as UserInvitation[])
            : [];

          if (isAppending) {
            setUserInvitations((prev) => {
              const existingIds = new Set(prev.map((item) => item.id));
              const uniqueNewItems = newItems.filter(
                (item) => !existingIds.has(item.id)
              );
              return [...prev, ...uniqueNewItems];
            });
          } else {
            setUserInvitations(newItems);
          }

          setHasMore(newItems.length === targetPageSize);
          setCurrentPage(targetPage);
          currentPageRef.current = targetPage;
          setIsLoaded(true);
          setLastError(null);
          return result;
        }

        setLastError(result);
        setIsLoaded(true);
        if (!isAppending) setUserInvitations([]);
        return result;
      } catch (error) {
        if (callId !== listCallIdRef.current) {
          return OpResult.fromException(error);
        }

        const result = OpResult.fromException(error);
        setLastError(OpResult.fromException(error));
        setIsLoaded(true);
        if (!isAppending) setUserInvitations([]);
        return result;
      } finally {
        if (callId === listCallIdRef.current) {
          if (isAppending) {
            setLoadingMore(false);
          } else {
            setLoading(false);
          }
        }
      }
    },
    []
  );

  // Get many invitations by IDs
  const getMany = useCallback(
    async (ids: string[]): Promise<OpResult> => {
      if (ids.length === 0) {
        return new OpResult();
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await UserInvitationService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedItems = Array.isArray(result.data)
            ? (result.data as UserInvitation[])
            : [];
          loadedItems.forEach((item) => {
            updateOrAddInList(item);
          });
          setLastError(null);
          return result;
        }

        setLastError(result);
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(OpResult.fromException(error));
        return result;
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddInList]
  );

  // ==========================================
  // Mutation Operations
  // ==========================================

  // Create invitation
  const create = useCallback(
    async (params: UserInvitationInput): Promise<OpResult> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await UserInvitationService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItem = (result.data as UserInvitation[])[0];
          setUserInvitation(newItem);
          setUserInvitations((prev) => [newItem, ...prev]);
          setLastError(null);
          return result;
        }

        setLastError(result);
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(OpResult.fromException(error));
        return result;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update invitation
  const update = useCallback(
    async (id: string, params: UserInvitationInput): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Invitation ID is required for updating"
        );
        setLastError(result);
        return result;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await UserInvitationService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedItem = (result.data as UserInvitation[])[0];
          setUserInvitation(updatedItem);
          updateOrAddInList(updatedItem);
          setLastError(null);
          return result;
        }

        setLastError(result);
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(OpResult.fromException(error));
        return result;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddInList]
  );

  // Remove invitation
  const remove = useCallback(
    async (id: string): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Invitation ID is required for removal"
        );
        setLastError(result);
        return result;
      }

      setRemovingId(id);
      setLastError(null);

      try {
        const result = await UserInvitationService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setUserInvitation(null);
          removeFromList(id);
          setLastError(null);
          return result;
        }

        setLastError(result);
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(OpResult.fromException(error));
        return result;
      } finally {
        setRemovingId(null);
      }
    },
    [removeFromList]
  );

  // ==========================================
  // Domain-Specific Operations
  // ==========================================

  // Resend invitation email
  const resend = useCallback(
    async (id: string): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Invitation ID is required for resending"
        );
        setLastError(result);
        return result;
      }

      setIsResending(true);
      setResendingId(id);
      setLastError(null);

      try {
        const result = await UserInvitationService.resend(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedItem = (result.data as UserInvitation[])[0];
          setUserInvitation(updatedItem);
          updateOrAddInList(updatedItem);
          setLastError(null);
          return result;
        }

        setLastError(result);
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(OpResult.fromException(error));
        return result;
      } finally {
        setIsResending(false);
        setResendingId(null);
      }
    },
    [updateOrAddInList]
  );

  // Accept invitation (public/unauthenticated flow)
  const acceptInvitation = useCallback(
    async (params: UserInvitationAcceptInput): Promise<OpResult> => {
      if (!params) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "User details are required for accepting invitation"
        );
        setLastError(result);
        return result;
      }

      setIsAcceptingInvitation(true);
      setLastError(null);

      try {
        const result = await UserInvitationService.acceptInvitation(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          setLastError(null);
          return result;
        }

        setLastError(result);
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(OpResult.fromException(error));
        return result;
      } finally {
        setIsAcceptingInvitation(false);
      }
    },
    []
  );

  // ==========================================
  // Convenience Methods
  // ==========================================

  // Load more — uses refs to avoid stale closures
  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) return;

    const nextPage = currentPageRef.current + 1;
    await list(
      lastQueriedFiltersRef.current,
      { page: nextPage, pageSize: pageSizeRef.current },
      lastQueriedSortRef.current,
      false
    );
  }, [list, loading, loadingMore, hasMore]);

  // Refresh — reset to page 1 with last-queried filters/sort
  const refresh = useCallback(async (): Promise<OpResult> => {
    return list(
      lastQueriedFiltersRef.current,
      { page: 1, pageSize: pageSizeRef.current },
      lastQueriedSortRef.current,
      true
    );
  }, [list]);

  // ROADMAP #131 — a save that timed out may still have landed: re-read what is shown.
  useRefreshOnWriteTimeout(refresh, isLoaded);

  // ==========================================
  // Derived Data Helpers
  // ==========================================

  const getInvitationById = useCallback(
    (id: string | undefined): UserInvitation | undefined => {
      if (!id) return undefined;
      return userInvitations.find((item) => item.id === id);
    },
    [userInvitations]
  );

  // ==========================================
  // Effects
  // ==========================================

  // Handle user change — reset and reload when userId changes
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    // Skip on initial mount
    if (previousUserId === undefined) {
      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          initialSortRef.current,
          true
        );
      }
      return;
    }

    // User changed (including sign out)
    if (previousUserId !== userId) {
      reset();

      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          initialSortRef.current,
          true
        );
      }
    }
  }, [userId, autoLoad, list, reset]);

  // ==========================================
  // Return
  // ==========================================

  return useMemo(
    () => ({
      // Data state
      userInvitation,
      userInvitations,
      loading,
      isLoaded,
      loadingMore,
      hasMore,
      isEmpty,

      // Search and filters
      filters,
      sortBy,

      // Pagination
      currentPage,
      pageSize: pageSizeRef.current,

      // Operations state
      isCreating,
      isUpdating,
      isResending,
      isAcceptingInvitation,
      removingId,
      resendingId,
      selectedUserInvitation,

      // Core operations
      get,
      list,
      getMany,
      create,
      update,
      remove,

      // Domain-specific operations
      resend,
      acceptInvitation,

      // Convenience methods
      loadMore,
      refresh,

      // UI state management
      setSelectedUserInvitation,

      // Helper operations
      reset,

      // Computed values
      invitationsByStatus,
      invitationsByEmailDomain,

      // Lookup helpers
      getInvitationById,

      // Error handling
      lastError,
      clearError,
    }),
    [
      userInvitation,
      userInvitations,
      loading,
      isLoaded,
      loadingMore,
      hasMore,
      isEmpty,
      filters,
      sortBy,
      currentPage,
      isCreating,
      isUpdating,
      isResending,
      isAcceptingInvitation,
      removingId,
      resendingId,
      selectedUserInvitation,
      get,
      list,
      getMany,
      create,
      update,
      remove,
      resend,
      acceptInvitation,
      loadMore,
      refresh,
      reset,
      invitationsByStatus,
      invitationsByEmailDomain,
      getInvitationById,
      lastError,
      clearError,
    ]
  );
};